// Account management: data export + account deletion.
// Apple requires in-app account deletion (App Store Review Guideline 5.1.1(v))
// and GDPR/CCPA both expect a self-serve data export.

import { useState } from "react";
import { Download, Trash2 } from "lucide-react";
import { deleteMyAccount, downloadAsJson, exportUserData } from "../lib/account";
import { friendlyError } from "../lib/errors";

export default function AccountCard() {
  const [exporting, setExporting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [confirmStep, setConfirmStep] = useState(0); // 0 = collapsed, 1 = ask, 2 = type-to-confirm
  const [confirmText, setConfirmText] = useState("");

  async function handleExport() {
    setExporting(true);
    setError(null);
    try {
      const data = await exportUserData();
      downloadAsJson(data);
    } catch (err) {
      setError(friendlyError(err));
    } finally {
      setExporting(false);
    }
  }

  async function handleDelete() {
    setDeleting(true);
    setError(null);
    try {
      await deleteMyAccount();
      // Session ends → AuthGate will swap us to SignIn. Reload to flush any
      // in-memory React state pointing at the now-gone user.
      window.location.replace("/");
    } catch (err) {
      setError(friendlyError(err));
      setDeleting(false);
    }
  }

  return (
    <section className="p-4 rounded-2xl bg-slate-900 border border-slate-800 space-y-3">
      <div>
        <div className="text-xs text-slate-400 uppercase tracking-wide">Your data</div>
        <div className="text-xs text-slate-500 mt-0.5">
          Download everything you've logged, or close your account permanently.
        </div>
      </div>

      <button
        type="button"
        onClick={handleExport}
        disabled={exporting}
        className="w-full py-2.5 rounded-lg border border-slate-700 text-slate-200 text-sm font-medium flex items-center justify-center gap-2 disabled:opacity-50"
      >
        <Download className="w-4 h-4" />
        {exporting ? "Preparing…" : "Download my data (JSON)"}
      </button>

      {confirmStep === 0 && (
        <button
          type="button"
          onClick={() => {
            setConfirmStep(1);
            setError(null);
          }}
          className="w-full py-2.5 rounded-lg border border-red-900 text-red-300 text-sm font-medium flex items-center justify-center gap-2"
        >
          <Trash2 className="w-4 h-4" />
          Delete account
        </button>
      )}

      {confirmStep === 1 && (
        <div className="p-3 rounded-lg bg-red-950/50 border border-red-900 space-y-2">
          <div className="text-sm text-red-200">
            This permanently deletes your meals, goals, weights, water log, favorites, and photos. <strong>Cannot be undone.</strong>
          </div>
          <div className="text-xs text-red-300/80">
            Consider downloading your data first.
          </div>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => setConfirmStep(0)}
              className="flex-1 py-2 rounded border border-slate-700 text-slate-300 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => setConfirmStep(2)}
              className="flex-1 py-2 rounded bg-red-700 text-white text-sm font-medium"
            >
              Continue
            </button>
          </div>
        </div>
      )}

      {confirmStep === 2 && (
        <div className="p-3 rounded-lg bg-red-950/50 border border-red-900 space-y-2">
          <label className="block">
            <span className="text-xs text-red-200">
              Type <span className="font-mono bg-red-900/40 px-1.5 rounded">DELETE</span> to confirm
            </span>
            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              autoCapitalize="characters"
              spellCheck={false}
              autoCorrect="off"
              className="mt-1 w-full bg-slate-900 border border-red-800 rounded p-2 text-slate-100 font-mono text-sm focus:outline-none focus:border-red-500"
            />
          </label>
          <div className="flex gap-2 pt-1">
            <button
              type="button"
              onClick={() => {
                setConfirmStep(0);
                setConfirmText("");
              }}
              className="flex-1 py-2 rounded border border-slate-700 text-slate-300 text-sm"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleDelete}
              disabled={deleting || confirmText.trim() !== "DELETE"}
              className="flex-1 py-2 rounded bg-red-600 text-white text-sm font-semibold disabled:opacity-40"
            >
              {deleting ? "Deleting…" : "Delete forever"}
            </button>
          </div>
        </div>
      )}

      {error && (
        <div className="p-2 rounded bg-red-950 border border-red-800 text-red-200 text-xs">
          {error}
        </div>
      )}
    </section>
  );
}
